/**
 * Device registrations: naming the device, and attributing the machine that was joined.
 *
 * The origin is an operator reading a directory-audit row on IR-20260818-Controller-001:
 * "i believe it was the attacker but this row doesn't indicate it either way. is that
 * because we don't know if it was attacker or because the row data displayed needs
 * adjusted?" -- BOTH, and the second was ours.
 *
 * The row read:
 *
 *     Device Registration Service performed Add registered owner to device
 *     'controller@milmar.net'          IP Address —
 *
 * It named the OWNER BEING ADDED and never named the device, although the device is right
 * there in `RawLog.targetResources[0].modifiedProperties` as `Device.DisplayName` /
 * `Device.ObjectID`. Six such rows on that case, and every one of them is an attacker
 * machine joined to the tenant -- which grants a Primary Refresh Token, i.e. persistence
 * that a password reset alone does not clear. That is one of the strongest remediation
 * findings a case can carry, and it was rendering as an unattributable Microsoft row.
 *
 * 🔴 THE ROW GENUINELY CANNOT BE ATTRIBUTED BY IP. `initiatedBy` is the app
 * `Device Registration Service`, the User-Agent is `Microsoft.OData.Client/7.12.5`, and
 * `IPAddress` is empty -- there is no address to classify and no session to corroborate.
 * The only route to attribution is the DEVICE it created, which is why
 * `attackerAttribution.js` grew a third basis rather than this being a display fix alone.
 *
 * Everything here is derived from `RawLog`, which the runbook already emits, so no
 * investigation re-run is needed for existing cases.
 */

/**
 * Directory-audit operations that act on a device object.
 *
 * Matches the runbook's own security-relevant list (`Invoke-SecurityInvestigation.ps1`,
 * "Device management"): `add registered owner to device`,
 * `remove registered owner from device`, `add registered users to device`,
 * `delete device`. The extra entries here are Entra siblings we do not currently collect --
 * listed so that a widening of the runbook's list does not silently fall back to the
 * generic "X performed Y on 'Z'" wording.
 */
export const DEVICE_OPS = new Set([
  'add registered owner to device',
  'remove registered owner from device',
  'add registered users to device',
  'remove registered users from device',
  'add device',
  'update device',
  'delete device',
]);

/** Operations that CREATE the trust -- the persistence-bearing subset of DEVICE_OPS. */
export const DEVICE_REGISTRATION_OPS = new Set([
  'add registered owner to device',
  'add registered users to device',
  'add device',
]);

const opKey = (op) => String(op || '').trim().toLowerCase();

export const isDeviceOp = (op) => DEVICE_OPS.has(opKey(op));
export const isDeviceRegistrationOp = (op) => DEVICE_REGISTRATION_OPS.has(opKey(op));

/**
 * Strip the JSON quoting Entra puts on a modifiedProperties value.
 *
 * 🔴 `newValue` arrives as the six characters `"DESKTOP"` including its own double
 * quotes -- the literal value CONTAINS them. Rendering it unstripped puts quote marks
 * inside the sentence.
 */
function unquote(v) {
  if (v == null) return '';
  let s = String(v).trim();
  if (s.length > 1 && s.startsWith('[') && s.endsWith(']')) s = s.slice(1, -1).trim();
  if (s.length > 1 && s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1).trim();
  return s;
}

/**
 * Read a named `modifiedProperties` entry from ANY target resource.
 *
 * Deliberately not limited to `targetResources[0]` the way `getDirAuditDetail` is: on a
 * registration the properties hang off the USER (index 0) and the device is a bare
 * reference at index 1, but a `delete device` names the device itself, and betting on the
 * index would work on one and silently return nothing on the other.
 */
function modifiedProperty(event, name) {
  const list = event?.RawLog?.targetResources;
  if (!Array.isArray(list)) return '';
  for (const tr of list) {
    const props = tr?.modifiedProperties;
    if (!Array.isArray(props)) continue;
    for (const p of props) {
      if (String(p?.displayName || '') !== name) continue;
      const v = unquote(p.newValue) || unquote(p.oldValue);
      if (v) return v;
    }
  }
  return '';
}

/** The device a directory-audit event acted on, or '' when the row names none. */
export function dirAuditDeviceName(event) {
  const named = modifiedProperty(event, 'Device.DisplayName');
  if (named) return named;
  // `delete device` and friends can carry the device as the target itself.
  const list = event?.RawLog?.targetResources;
  if (Array.isArray(list)) {
    for (const tr of list) {
      if (String(tr?.type || '').toLowerCase() === 'device' && tr?.displayName) {
        return String(tr.displayName);
      }
    }
  }
  return '';
}

/** The device object id, which is the stable identity for remediation. */
export function dirAuditDeviceObjectId(event) {
  const id = modifiedProperty(event, 'Device.ObjectID');
  if (id) return id;
  const list = event?.RawLog?.targetResources;
  if (Array.isArray(list)) {
    for (const tr of list) {
      if (String(tr?.type || '').toLowerCase() === 'device' && tr?.id) return String(tr.id);
    }
  }
  return '';
}

/**
 * Every device name an event references, lower-cased and de-duplicated.
 *
 * 🔴 THREE FIELD NAMES, MEASURED -- not a defensive guess. Interactive sign-ins carry
 * `DeviceHostname`; the non-interactive feed carries `DeviceName`; directory audits carry
 * neither and must be read out of `RawLog`. Reading one name would have produced a clean,
 * confident, EMPTY inventory (memory `feedback_wrong_value_that_reassures_hides`).
 *
 * 🔴 AND THE VALUE CAN BE A COMMA-JOINED LIST. The non-interactive feed aggregates, so a
 * single row carries `DESKTOP-2CED23,DESKTOP-72C443,DESKTOP-A9742F,DESKTOP-F0F39B`.
 * Measured on the reference case: `DESKTOP-5C2048` appears ONLY inside such a list, so
 * without the split that attacker machine is missing from the inventory entirely.
 */
export function eventDeviceNames(event) {
  if (!event) return [];
  const raw = [
    event.DeviceHostname,
    event.DeviceName,
    event.DeviceDisplayName,
    dirAuditDeviceName(event),
  ];
  const out = new Set();
  for (const v of raw) {
    if (!v) continue;
    for (const part of String(v).split(',')) {
      const p = part.trim().toLowerCase();
      if (p) out.add(p);
    }
  }
  return [...out];
}

/**
 * The AAD device GUIDs a row identifies, lower-cased.
 *
 * 🔴 THIS IS AN IDENTITY; `eventDeviceNames` returns a LABEL. A name arrives under three
 * different field names, can be renamed, and can collide; the GUID cannot. Measured on
 * tenant c65417d2 (2026-08-25) the GUID is present on EXACTLY the rows that carry a name
 * (270/270 interactive, 2014/2014 non-interactive on the reference case), so preferring
 * identity costs no coverage at all.
 *
 * Comma-split for the same reason the names are: the non-interactive feed aggregates to
 * (session, ip, hour), so one row legitimately spans several machines.
 *
 * ⚠️ `DeviceAadId` -- NOT `DeviceId`, which is a mailbox-access identifier on Exchange rows
 * (v1.151.0) and a different thing entirely. Reading the wrong one yields an association
 * that is wrong rather than merely absent.
 *
 * Absent on every results blob written before the runbook carried it, so a pre-existing
 * case yields [] here and falls through to the name path.
 */
export function eventDeviceIds(event) {
  if (!event) return [];
  const out = new Set();
  for (const v of [event.DeviceAadId, event.DeviceAadIds]) {
    if (!v) continue;
    for (const part of String(v).split(',')) {
      const p = part.trim().toLowerCase();
      if (p) out.add(p);
    }
  }
  return [...out];
}

/**
 * The device name as Microsoft recorded it, for display.
 *
 * `eventDeviceNames` lower-cases so that the inventory and the lookup share one key
 * space; a label built from that key would print `desktop-2ced23`, which is not what the
 * record says. Returns the matching original-cased value, falling back to the key.
 */
export function eventDeviceLabel(event, lowerName) {
  const want = String(lowerName || '').toLowerCase();
  if (!want) return '';
  const raw = [event?.DeviceHostname, event?.DeviceName, event?.DeviceDisplayName, dirAuditDeviceName(event)];
  for (const v of raw) {
    if (!v) continue;
    for (const part of String(v).split(',')) {
      const p = part.trim();
      if (p.toLowerCase() === want) return p;
    }
  }
  return want;
}

/**
 * The action row's device identity, used to keep TWO devices out of ONE action.
 *
 * `directoryActions.js` groups on (target object, 30s window), and the target of a
 * registration is the USER -- so `DESKTOP-2CED23` at 13:36:47 and `DESKTOP-A9742F` at
 * 13:36:50 merged into a single four-row action that could name only one of them. Two
 * device objects created is two facts, and the operator's finding is a COUNT ("six
 * devices"), so a merge that renders five rows is a wrong answer to the question being
 * asked. Returns '' for every non-device row, which leaves all other grouping unchanged.
 */
export function deviceActionDiscriminator(event) {
  if (!isDeviceOp(event?.Operation)) return '';
  return (dirAuditDeviceObjectId(event) || dirAuditDeviceName(event)).toLowerCase();
}

/**
 * The ONE phrasing helper for device directory-audit rows.
 *
 * `generateDescription` exists in two copies (`utils/eventUtils.js` and
 * `components/TimelineView.jsx`) which hold the mask flag with OPPOSITE polarity and only
 * one of which is exported. Both delegate here, and
 * `frontend/scripts/deviceRegistration.test.mjs` asserts statically that they do -- the
 * established remedy, as for `describeExchangeItemChange` / `describeMessageTrace`.
 *
 * Returns '' when the row is not a device operation or carries no device name, so the
 * caller falls through to its existing wording rather than emitting a partial sentence.
 */
export function describeDeviceRegistration(event, targetLabel = '') {
  const op = opKey(event?.Operation);
  if (!DEVICE_OPS.has(op)) return '';
  const device = dirAuditDeviceName(event);
  if (!device) return '';

  // Name a HUMAN initiator only. The initiator on these rows is normally the app
  // `Device Registration Service`, which is Microsoft's plumbing and not the actor -- a
  // real admin doing this on someone else's behalf is worth naming, a service is not.
  const initiator = event?.RawLog?.initiatedBy?.user?.userPrincipalName || '';
  const who = targetLabel || '';
  const by = (initiator && initiator.toLowerCase() !== who.toLowerCase()) ? ` by ${initiator}` : '';
  const to = who ? ` to ${who}` : '';
  const forWhom = who ? ` for ${who}` : '';

  switch (op) {
    case 'add registered owner to device':
      return `Registered device ${device}${to}${by}`;
    case 'add registered users to device':
      return `Added ${who || 'a user'} as a registered user of device ${device}${by}`;
    case 'remove registered owner from device':
      return `Removed ${who || 'the owner'} as registered owner of device ${device}${by}`;
    case 'remove registered users from device':
      return `Removed ${who || 'a user'} as a registered user of device ${device}${by}`;
    case 'add device':
      return `Device ${device} added to the directory${forWhom}${by}`;
    case 'update device':
      return `Device ${device} updated${by}`;
    case 'delete device':
      return `Device ${device} deleted from the directory${by}`;
    default:
      return '';
  }
}
